import * as React from 'react';
import { Spin, TreeSelect } from 'antd';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { ReactNode } from 'react';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { isEqual } from 'lodash';

const find = (array, key) => {
	var object;

	array.some(function f(a) {
		if (a.value === key) {
			object = a;
			return true;
		}
		if (Array.isArray(a.children) && a.children.length) {
			return a.children.some(f);
		}
	});
	return object;
};

const collectLeafValues = (node) =>
	Array.isArray(node.children) && node.children.length > 0
		? node.children.reduce((acc, child) => acc.concat(collectLeafValues(child)), [])
		: [node.value];

// Collapse a set of selected leaf values into the fewest nodes that represent them: a parent stands
// in for its whole branch when every leaf beneath it is selected, otherwise the individual leaves
// are kept. A SHOW_PARENT TreeSelect that is handed only leaf ids hides them all and renders blank;
// handing it the collapsed parent keys instead makes it show a single parent tag.
const collapseSelectionToNodes = (treeData = [], selectedValues = []) => {
	const selected = new Set(selectedValues);
	const consumedByParent = new Set();
	const collapsed = [];
	const walk = (nodes) => {
		nodes.forEach((node) => {
			const hasChildren = Array.isArray(node.children) && node.children.length > 0;
			if (!hasChildren) return;
			const leaves = collectLeafValues(node);
			if (leaves.length > 0 && leaves.every((leaf) => selected.has(leaf))) {
				collapsed.push(node.value);
				leaves.forEach((leaf) => consumedByParent.add(leaf));
			} else {
				walk(node.children);
			}
		});
	};
	walk(treeData);
	// Keep every selected value that a collapsed parent didn't already cover — including ids whose
	// branch isn't loaded yet — so a selection is never silently dropped (which would blank the field).
	selectedValues.forEach((val) => {
		if (!consumedByParent.has(val)) collapsed.push(val);
	});
	return collapsed;
};

// A value is only stripped when we can positively identify it as a non-leaf (parent) node in the
// currently-loaded tree. Ids we haven't loaded yet (e.g. a saved selection whose branch lives on a
// later page of results) are preserved — otherwise the field silently drops selections that the
// form still holds, which is what made it render blank while the count still read "Selected: (N)".
const isResolvedParent = (treeData, val) => {
	const record = find(treeData, val);
	return !!record && nullSafeGetOrElse('children', record, []).length > 0;
};

const getCleanedValues = (treeData = [], value, mode) => {
	if (mode === 'multiple') {
		const allValues = value || [];
		return allValues.filter((val) => !isResolvedParent(treeData, val));
	} else if (value) {
		return isResolvedParent(treeData, value) ? undefined : value;
	}
};

interface ProblemTypeTreeSelectProps {
	stateSlice: any;
	targetCollectionName: string;
	placeholder?: string;
	disabledPlaceholder?: string;
	valueAccessor?: any;
	additionalFilters?: any;
	initialPagination?: any;
	allowClear?: boolean;
	size?: 'large' | 'small';
	style?: any;
	mode?: 'default' | 'multiple' | 'tags' | 'combobox';
	value?: any;
	onChange?: any;
	loading?: boolean;
	disabled?: boolean;
	emptyWhenDisabled?: boolean;
	sortBy?: any;
	// When the field stores leaf ids but should render the collapsed parent tag, set this so the
	// value handed to antd is collapsed to parent keys (otherwise SHOW_PARENT hides the leaves and
	// the field renders blank). Off by default to preserve existing consumers' behavior.
	collapseSelectionForDisplay?: boolean;
	getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;

	fetchMultiple?: { (ids: string[] | number[], targetCollectionName?: string): void };

	renderRecord?: { (data: any): ReactNode };

	fetchData(
		searchText: string,
		targetCollectionName: string,
		pagination?: any,
		sorting?: any,
		filters?: any,
		addToTargetCollection?: boolean
	): Promise<any[]>;
}

const defaultPagination = { current: 1, pageSize: 50 };
const { SHOW_PARENT } = TreeSelect;
const arrDataToTreeData = (arrData, mode) => {
	let mapData = {};
	let childIds = [];
	const transformRecord = (el) => ({
		value: el.id,
		key: el.id,
		title: el.name,
		hierarchyName: el.hierarchyName || el.name,
		selectable:
			mode === 'multiple' || (el.id && !(Array.isArray(el.children) && el.children.length)),
		children:
			Array.isArray(el.children) && el.children.length > 0
				? arrDataToTreeData(el.children, mode)
				: [],
	});
	const recs = arrData.map((el) => transformRecord(el));
	return recs.filter((rec) => {
		return !childIds.includes(rec.value);
	});
};

export default class ProblemTypeTreeSelect extends React.Component<
	ProblemTypeTreeSelectProps,
	any
> {
	constructor(props) {
		super(props);
		const value = props.value;
		this.state = {
			value: value,
			treeData: [],
		};
	}

	componentDidUpdate(prevProps) {
		// Should be a controlled component.
		const { mode } = this.props;
		if ('value' in this.props && !isEqual(this.props.value, prevProps.value)) {
			const value =
				nullSafeGetOrElse('state.treeData', this, []).length > 0
					? getCleanedValues(this.state.treeData, this.props.value, mode)
					: this.props.value;
			this.setState({ value });
		}
		if (!isEqual(this.props.additionalFilters, prevProps.additionalFilters)) {
			this.props
				.fetchData(
					this.state.searchText && this.state.searchText.trim(),
					this.props.targetCollectionName,
					this.props.initialPagination || defaultPagination,
					this.props.sortBy,
					this.props.additionalFilters,
					false
				)
				.then((arrData) => {
					const treeData = arrDataToTreeData(arrData, this.props.mode);
					this.setState({ treeData });
				});
		}
	}

	componentDidMount() {
		const { targetCollectionName, mode, fetchMultiple } = this.props;
		this.props
			.fetchData(
				'',
				targetCollectionName,
				this.props.initialPagination || defaultPagination,
				this.props.sortBy,
				this.props.additionalFilters,
				false
			)
			.then((arrData) => {
				const treeData = arrDataToTreeData(arrData, this.props.mode);
				// Read the LIVE form value here, never a value captured at mount: the form usually
				// propagates the field value after mount, so a captured value would be stale (empty)
				// and would wipe the real selection when we setState below.
				const liveValue = 'value' in this.props ? this.props.value : this.state.value;
				const selectedIds = Array.isArray(liveValue)
					? liveValue
					: liveValue != null
					? [liveValue]
					: [];
				// Load records for the selected ids so their tags render even when their branch isn't
				// in the loaded tree (e.g. children that load lazily on expand).
				if (selectedIds.length > 0 && fetchMultiple) {
					fetchMultiple(selectedIds, targetCollectionName);
				}
				const cleanedValues = getCleanedValues(treeData, liveValue, mode);
				this.setState({ treeData, value: cleanedValues });
			});
	}

	handleValueChange = (values) => {
		const { mode } = this.props;
		const valueSet = new Set();

		function addAllChildren(array) {
			array.map(function f(a) {
				if (Array.isArray(a.children) && a.children.length) {
					a.children.map(f);
				} else {
					valueSet.add(a.value);
				}
			});
		}

		if (mode === 'multiple') {
			values.map((key) => {
				const entity = find(this.state.treeData, key);
				if (entity) {
					if (entity.children && entity.children.length) {
						addAllChildren(entity.children);
					} else {
						valueSet.add(entity.value);
					}
				}
			});
		}
		const updatedValue = mode === 'multiple' ? Array.from(valueSet) : values;
		if (!('value' in this.props)) {
			this.setState({ value: updatedValue });
		}
		this.triggerChange(updatedValue);
	};

	triggerChange = (changedValue) => {
		// Should provide an event to pass value to Form.
		const onChange = this.props.onChange;
		if (onChange) {
			onChange(changedValue);
		}
	};

	render() {
		const { value } = this.state;
		const {
			disabled,
			allowClear = false,
			loading,
			emptyWhenDisabled,
			valueAccessor = (el) => el.id,
			stateSlice,
			targetCollectionName,
			mode,
			placeholder,
			disabledPlaceholder,
			size,
			style,
			tagRender,
			collapseSelectionForDisplay = false,
			getPopupContainer,
		} = this.props;
		const selectedVals = Array.isArray(value) ? value : [value];
		const selectedRecords = selectedVals
			.map((v) => stateSlice.records[v])
			.filter((r) => r && valueAccessor(r));
		const dropdownRecords = getRecordsForTargetCollection(stateSlice, targetCollectionName);
		const visibleRecords = selectedRecords.concat(
			dropdownRecords.filter((r) => !selectedVals.some((val) => valueAccessor(r) == val))
		);
		const fetching = stateSlice.fetching;
		const noVisibleRecordsWithoutSearch =
			nullSafeGetOrElse('searchText.length', this.state, 0) === 0 && visibleRecords.length === 0;

		// Ensure every selected id has a node antd can render a tag for. Selections whose branch isn't
		// in the loaded tree (e.g. on a later page) are appended as flat nodes built from the records
		// loaded via fetchMultiple, so the field shows them instead of rendering blank. Once the real
		// branch loads, find() locates the node in treeData and we skip the duplicate.
		const missingSelectedNodes = selectedRecords
			.filter((record) => !find(this.state.treeData, record.id))
			.map((record) => ({
				value: record.id,
				key: record.id,
				title: record.name,
				hierarchyName: record.hierarchyName || record.name,
				selectable: true,
				children: [],
			}));
		const effectiveTreeData =
			missingSelectedNodes.length > 0
				? [...missingSelectedNodes, ...this.state.treeData]
				: this.state.treeData;

		// SHOW_PARENT only renders a parent tag when it is handed the parent key. The field stores
		// leaf ids, so collapse them to parent keys for display (the form/backend value stays leaves).
		const displayValue = collapseSelectionForDisplay
			? collapseSelectionToNodes(effectiveTreeData, selectedVals)
			: value;

		return (
			<TreeSelect
				multiple={mode === 'multiple'}
				treeCheckable={mode === 'multiple'}
				notFoundContent={fetching ? <Spin size="small" /> : null}
				placeholder={disabled || noVisibleRecordsWithoutSearch ? disabledPlaceholder : placeholder}
				loading={loading}
				showSearch={true}
				dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
				treeNodeFilterProp="title"
				onChange={this.handleValueChange}
				treeData={disabled && emptyWhenDisabled ? [] : effectiveTreeData}
				size={size}
				style={style}
				allowClear={allowClear}
				value={disabled && emptyWhenDisabled ? [] : displayValue}
				disabled={disabled || noVisibleRecordsWithoutSearch}
				treeNodeLabelProp="hierarchyName"
				showCheckedStrategy={'SHOW_PARENT'}
				treeDefaultExpandedKeys={[`${value}`]}
				tagRender={tagRender}
				getPopupContainer={getPopupContainer}
			/>
		);
	}
}
